import React, { useMemo, useState } from 'react'
import { ScrollView } from 'react-native'

import { useRoute } from '@react-navigation/native'
import { MotiView } from '@zet/common/components/motiView'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import {
  B5,
  BTN,
  H1,
  H5,
  Icon,
  SH1,
  SH3,
  Stack,
  XStack,
  colors,
} from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { useRecommendation } from '@zet/feature-brands/api/brands/hooks'
import ProductCard from '@zet/feature-brands/components/productCard'
import { useCreditReport } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { Skeleton } from 'moti/skeleton'

import CreditInfoBottomSheet from '../../components/creditInfoBottomSheet'
import CreditScoreCircleInfo from '../../components/creditScoreAnimatedCircle'
import CreditScoreBenefits from '../../components/creditScoreBenefits'
import CreditScoreFaq from '../../components/creditScoreFaq'

interface IRoute {
  customerId: number
  phoneNumber: string
  brandSpecificPartnerId: string
}

const CreditReport = () => {
  const route = useRoute()
  const params: IRoute = route.params as IRoute
  const { data } = useRecommendation(params?.customerId)
  const navigation = useNavigation()
  const {
    data: creditReport,
    isLoading,
    isFetched,
    isError,
  } = useCreditReport()

  const [isCreditBottomSheetVisible, setIsCreditBottomSheetVisible] =
    useState<boolean>(false)

  const topRecommendation = useMemo(() => {
    return data?.recommendations[data?.sortedCategoryList?.[1]?.categoryId]
      ?.eligiblePartners
  }, [data])

  const activeAccounts = Number(creditReport?.activeAccounts)
  const PersonalLoansAccounts = Number(creditReport?.personalLoans)
  const CreditCardsAcounts = Number(creditReport?.creditCards)
  const OtherAccounts =
    activeAccounts - (PersonalLoansAccounts + CreditCardsAcounts)

  const Spacer = ({ height = 8 }) => (
    <Stack className="bg-grays-20" style={{ height }} />
  )

  const handleInfoPress = () => {
    setIsCreditBottomSheetVisible(true)
  }

  const handleCreditDismissBottomSheet = () => {
    setIsCreditBottomSheetVisible(false)
  }

  const handlePress = () => {
    navigation.navigate('BrandPage', {
      customerId: params?.customerId,
      customerPhoneNumber: params?.phoneNumber,
      isBureauReportExpired: false,
      brandSpecificPartnerId: params?.brandSpecificPartnerId,
    })
  }

  if (isLoading) {
    return (
      <MotiView
        transition={{
          type: 'timing',
        }}
        style={{ height: 330, paddingHorizontal: 12 }}
        animate={{
          backgroundColor: colors.neutral.white100,
        }}
      >
        <Stack className="items-center justify-center">
          <Skeleton
            colorMode={'light'}
            radius="round"
            height={200}
            width={200}
          />
        </Stack>
        <Spacer />
        <Skeleton colorMode={'light'} width={'100%'} />
        <Spacer />
        <Skeleton colorMode={'light'} width={'100%'} />
      </MotiView>
    )
  }

  if ((isFetched && isError) || !creditReport?.creditScore) {
    return (
      <Stack className="flex-1 items-center justify-center">
        <H1>{i18n.t('noReport')}</H1>
      </Stack>
    )
  }

  return (
    <>
      <ScrollView
        className="flex-1 bg-white"
        showsVerticalScrollIndicator={false}
      >
        <XStack className="mb-3 items-center justify-between px-3 pt-3">
          <H5 className="text-grays-100">
            {i18n.t('checkCreditScore.yourScore')}
          </H5>
        </XStack>
        <CreditScoreCircleInfo
          handleInfoPress={handleInfoPress}
          name={creditReport?.fullName}
          bureau={creditReport?.bureau}
          creditScore={Number(creditReport?.creditScore)}
          bureauReportFetchDate={creditReport?.bureauReportFetchedAt}
        />
        {topRecommendation?.length ? (
          <Stack className="bg-white pt-3">
            <XStack className="items-center space-x-2 p-3 pt-0">
              <Stack>
                <Icon type="Star" color={colors.decorative.bee[100]} />
              </Stack>
              <SH1 className="text-grays-100">
                {i18n.t('checkCreditScore.eligibleForBrands')}
              </SH1>
            </XStack>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {topRecommendation?.slice(0, 3)?.map((value, index) => (
                <ProductCard
                  partnerId={value.partnerId}
                  key={value.partnerId}
                  index={index}
                  isRecommendationFlow
                  approvalRate={value?.approvalRate}
                  approvalRateTextColor={value?.approvalRateTextColor}
                  route={route}
                />
              ))}
            </ScrollView>
          </Stack>
        ) : (
          <></>
        )}
        <Spacer />
        <Stack className="mt-3">
          <SH1 className="p-3">
            {i18n.t('checkCreditScore.activeAccounts', {
              value: activeAccounts,
            })}
          </SH1>
          {PersonalLoansAccounts ? (
            <Stack className="border-grays-20 border-y p-3">
              <B5 className="font-[Graphie-Bold]">
                {i18n.t('checkCreditScore.activeAccounts', {
                  value: PersonalLoansAccounts,
                })}
              </B5>
              <SH3>{i18n.t('checkCreditScore.pl')}</SH3>
            </Stack>
          ) : (
            <></>
          )}
          {CreditCardsAcounts ? (
            <Stack className="border-grays-20 border-y p-3">
              <B5 className="font-[Graphie-Bold]">
                {i18n.t('checkCreditScore.activeAccounts', {
                  value: CreditCardsAcounts,
                })}
              </B5>
              <SH3>{i18n.t('checkCreditScore.cc')}</SH3>
            </Stack>
          ) : (
            <></>
          )}
          {OtherAccounts ? (
            <Stack className="border-grays-20 border-y p-3">
              <B5 className="font-[Graphie-Bold]">
                {i18n.t('checkCreditScore.activeAccounts', {
                  value: OtherAccounts,
                })}
              </B5>
              <SH3>{i18n.t('checkCreditScore.other')}</SH3>
            </Stack>
          ) : (
            <></>
          )}
        </Stack>
        <Spacer />
        <CreditScoreBenefits />
        <Spacer />
        <CreditScoreFaq bureau={creditReport?.bureau} />
      </ScrollView>
      <Stack
        className="border-grays-40 items-center justify-center border-t bg-white px-3"
        style={{ height: 70 }}
      >
        <Pressable
          eventName="click_action"
          eventPayload={{
            item_name: 'homepage_new_credit_score_all_recommendation',
          }}
          className="py-4/2 bg-primary-100 w-full rounded"
          onPress={handlePress}
        >
          <BTN className="text-center text-white">
            {i18n.t('checkCreditScore.bestOffer')}
          </BTN>
        </Pressable>
      </Stack>
      <CreditInfoBottomSheet
        visible={isCreditBottomSheetVisible}
        onDismiss={handleCreditDismissBottomSheet}
        bureau={creditReport?.bureau}
      />
    </>
  )
}

export default CreditReport
